import { useState } from 'react'
import {
  Ticket,
  Plus,
  Sparkles,
  Zap,
  MessageSquare,
  Crown,
  Copy,
  Check,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Filter,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'

// Types
interface GenerateCodeResponse {
  code: string
  style: string
}

interface CodeResponse {
  id: number
  code: string
  promotion_type: string
  description: string
  max_uses: number | null
  current_uses: number
  remaining_uses: number | null
  valid_from: string
  valid_until: string
  campaign: string | null
  is_active: boolean
  created_at: string
  promoted_tier_id: number | null
  promoted_tier_name: string | null
  promotion_days: number | null
  tool_calls_amount: number | null
  tool_calls_expires_months: number | null
  chat_messages_amount: number | null
  chat_messages_expires_with_cycle: boolean | null
}

type CodeStyle = 'random' | 'readable' | 'campaign'
type PromotionType = 'tier_access' | 'tool_calls' | 'chat_messages'
type SortField = 'created_at' | 'code' | 'promotion_type' | 'current_uses' | 'valid_until'
type SortDirection = 'asc' | 'desc'

// API functions
const generateCode = async (
  style: CodeStyle,
  prefix?: string,
  length?: number,
): Promise<GenerateCodeResponse> => {
  const response = await apiClient.post('/admin/promotional-codes/generate', {
    style,
    prefix,
    length: length || 6,
  })
  return response.data
}

const fetchPromotionalCodes = async (): Promise<CodeResponse[]> => {
  const response = await apiClient.get('/admin/promotional-codes/')
  return response.data
}

const toggleCodeActive = async (codeId: number, isActive: boolean): Promise<CodeResponse> => {
  const response = await apiClient.patch(`/admin/promotional-codes/${codeId}`, {
    is_active: isActive,
  })
  return response.data
}

const createTierAccessCode = async (data: {
  code: string
  promoted_tier_id: number
  promotion_days: number
  description: string
  valid_days: number
  max_uses?: number | null
  campaign?: string | null
}): Promise<CodeResponse> => {
  const response = await apiClient.post('/admin/promotional-codes/tier-access', data)
  return response.data
}

const createToolCallsCode = async (data: {
  code: string
  tool_calls_amount: number
  expires_months: number
  description: string
  valid_days: number
  max_uses?: number | null
  campaign?: string | null
}): Promise<CodeResponse> => {
  const response = await apiClient.post('/admin/promotional-codes/tool-calls', data)
  return response.data
}

const createChatMessagesCode = async (data: {
  code: string
  chat_messages_amount: number
  expires_with_cycle: boolean
  description: string
  valid_days: number
  max_uses?: number | null
  campaign?: string | null
}): Promise<CodeResponse> => {
  const response = await apiClient.post('/admin/promotional-codes/chat-messages', data)
  return response.data
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const isExpired = (validUntil: string) => {
  return new Date(validUntil) < new Date()
}

export function PromotionalCodesTab() {
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState<PromotionType>('tool_calls')
  const [generatedCode, setGeneratedCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  // Code generation state
  const [codeStyle, setCodeStyle] = useState<CodeStyle>('random')
  const [campaignPrefix, setCampaignPrefix] = useState('')

  // Common form state
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [validDays, setValidDays] = useState(30)
  const [maxUses, setMaxUses] = useState<number | null>(null)
  const [campaign, setCampaign] = useState('')

  // Tier access specific
  const [promotedTierId, setPromotedTierId] = useState(3)
  const [promotionDays, setPromotionDays] = useState(14)

  // Tool calls specific
  const [toolCallsAmount, setToolCallsAmount] = useState(100)
  const [expiresMonths, setExpiresMonths] = useState(12)

  // Chat messages specific
  const [chatMessagesAmount, setChatMessagesAmount] = useState(100)
  const [expiresWithCycle, setExpiresWithCycle] = useState(true)

  // Table state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterType, setFilterType] = useState<'all' | PromotionType>('all')

  const { data: codes = [], isLoading: codesLoading } = useQuery({
    queryKey: ['promotional-codes'],
    queryFn: fetchPromotionalCodes,
  })

  const generateMutation = useMutation({
    mutationFn: () =>
      generateCode(codeStyle, codeStyle === 'campaign' ? campaignPrefix : undefined),
    onSuccess: (data) => {
      setGeneratedCode(data.code)
      setCode(data.code)
      toast.success(`Generated: ${data.code}`)
    },
    onError: () => toast.error('Failed to generate code'),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ codeId, isActive }: { codeId: number; isActive: boolean }) =>
      toggleCodeActive(codeId, isActive),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] })
      toast.success(`Code ${data.code} ${data.is_active ? 'activated' : 'deactivated'}`)
    },
    onError: () => toast.error('Failed to update code'),
  })

  const createTierMutation = useMutation({
    mutationFn: createTierAccessCode,
    onSuccess: (data) => {
      toast.success(`Created tier access code: ${data.code}`)
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] })
      resetForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create code')),
  })

  const createToolCallsMutation = useMutation({
    mutationFn: createToolCallsCode,
    onSuccess: (data) => {
      toast.success(`Created tool calls code: ${data.code}`)
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] })
      resetForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create code')),
  })

  const createChatMutation = useMutation({
    mutationFn: createChatMessagesCode,
    onSuccess: (data) => {
      toast.success(`Created chat messages code: ${data.code}`)
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] })
      resetForm()
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to create code')),
  })

  const resetForm = () => {
    setCode('')
    setGeneratedCode('')
    setDescription('')
    setValidDays(30)
    setMaxUses(null)
    setCampaign('')
  }

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode || code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const copyTableCode = (codeText: string, id: number) => {
    navigator.clipboard.writeText(codeText)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
    toast.success('Code copied!')
  }

  const handleCreate = () => {
    if (!code || !description) {
      toast.error('Code and description are required')
      return
    }
    if (activeTab === 'tier_access') {
      createTierMutation.mutate({
        code,
        promoted_tier_id: promotedTierId,
        promotion_days: promotionDays,
        description,
        valid_days: validDays,
        max_uses: maxUses,
        campaign: campaign || null,
      })
    } else if (activeTab === 'tool_calls') {
      createToolCallsMutation.mutate({
        code,
        tool_calls_amount: toolCallsAmount,
        expires_months: expiresMonths,
        description,
        valid_days: validDays,
        max_uses: maxUses,
        campaign: campaign || null,
      })
    } else if (activeTab === 'chat_messages') {
      createChatMutation.mutate({
        code,
        chat_messages_amount: chatMessagesAmount,
        expires_with_cycle: expiresWithCycle,
        description,
        valid_days: validDays,
        max_uses: maxUses,
        campaign: campaign || null,
      })
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const filteredCodes = codes
    .filter((c) => {
      if (filterActive === 'active') return c.is_active && !isExpired(c.valid_until)
      if (filterActive === 'inactive') return !c.is_active || isExpired(c.valid_until)
      return true
    })
    .filter((c) => filterType === 'all' || c.promotion_type === filterType)
    .sort((a, b) => {
      let aVal: string | number = a[sortField]
      let bVal: string | number = b[sortField]
      if (sortField === 'created_at' || sortField === 'valid_until') {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1
    })

  const isCreating =
    createTierMutation.isPending ||
    createToolCallsMutation.isPending ||
    createChatMutation.isPending

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    )
  }

  const getPromotionBadge = (type: string) => {
    switch (type) {
      case 'tool_calls':
        return (
          <Badge variant="default" className="bg-blue-500">
            <Zap className="h-3 w-3 mr-1" />
            Tool Calls
          </Badge>
        )
      case 'chat_messages':
        return (
          <Badge variant="default" className="bg-purple-500">
            <MessageSquare className="h-3 w-3 mr-1" />
            Chat
          </Badge>
        )
      case 'tier_access':
        return (
          <Badge variant="default" className="bg-amber-500">
            <Crown className="h-3 w-3 mr-1" />
            Tier
          </Badge>
        )
      default:
        return <Badge>{type}</Badge>
    }
  }

  const getPromotionValue = (c: CodeResponse) => {
    if (c.promotion_type === 'tool_calls') return `${c.tool_calls_amount} calls`
    if (c.promotion_type === 'chat_messages') return `${c.chat_messages_amount} msgs`
    if (c.promotion_type === 'tier_access') return `${c.promoted_tier_name} (${c.promotion_days}d)`
    return '-'
  }

  return (
    <div className="space-y-6">
      {/* Existing Codes Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Existing Codes ({filteredCodes.length})
              </CardTitle>
              <CardDescription>Click on code to copy</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select
                value={filterActive}
                onValueChange={(v) => setFilterActive(v as 'all' | 'active' | 'inactive')}
              >
                <SelectTrigger className="w-[130px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={(v) => setFilterType(v as 'all' | PromotionType)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="tool_calls">Tool Calls</SelectItem>
                  <SelectItem value="chat_messages">Chat Messages</SelectItem>
                  <SelectItem value="tier_access">Tier Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {codesLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No promotional codes found</div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('code')}
                    >
                      Code <SortIcon field="code" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('promotion_type')}
                    >
                      Type <SortIcon field="promotion_type" />
                    </TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('current_uses')}
                    >
                      Uses <SortIcon field="current_uses" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('valid_until')}
                    >
                      Valid Until <SortIcon field="valid_until" />
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((c) => (
                    <TableRow
                      key={c.id}
                      className={!c.is_active || isExpired(c.valid_until) ? 'opacity-50' : ''}
                    >
                      <TableCell>
                        <button
                          onClick={() => copyTableCode(c.code, c.id)}
                          className="font-mono font-bold text-primary hover:underline flex items-center gap-1"
                        >
                          {c.code}
                          {copiedId === c.id ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3 opacity-50" />
                          )}
                        </button>
                        {c.campaign && (
                          <span className="text-xs text-muted-foreground block">{c.campaign}</span>
                        )}
                      </TableCell>
                      <TableCell>{getPromotionBadge(c.promotion_type)}</TableCell>
                      <TableCell className="font-medium">{getPromotionValue(c)}</TableCell>
                      <TableCell>
                        {c.current_uses} / {c.max_uses || '∞'}
                      </TableCell>
                      <TableCell>
                        <span className={isExpired(c.valid_until) ? 'text-red-500' : ''}>
                          {formatDate(c.valid_until)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isExpired(c.valid_until) ? (
                          <Badge variant="destructive">Expired</Badge>
                        ) : c.is_active ? (
                          <Badge variant="default" className="bg-green-500">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            toggleActiveMutation.mutate({ codeId: c.id, isActive: !c.is_active })
                          }
                          disabled={toggleActiveMutation.isPending || isExpired(c.valid_until)}
                        >
                          {c.is_active ? (
                            <ToggleRight className="h-4 w-4 text-green-500" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Code Generator Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Code Generator
          </CardTitle>
          <CardDescription>Generate a unique code or enter your own</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Style</Label>
              <Select value={codeStyle} onValueChange={(v) => setCodeStyle(v as CodeStyle)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random (X7K2M9)</SelectItem>
                  <SelectItem value="readable">Readable (BONUS-CALLS-47)</SelectItem>
                  <SelectItem value="campaign">Campaign (PREFIX-X7K2)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {codeStyle === 'campaign' && (
              <div className="w-[150px]">
                <Label>Prefix</Label>
                <Input
                  placeholder="SETAC2026"
                  value={campaignPrefix}
                  onChange={(e) => setCampaignPrefix(e.target.value.toUpperCase())}
                  maxLength={20}
                />
              </div>
            )}
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || (codeStyle === 'campaign' && !campaignPrefix)}
            >
              {generateMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Generate
            </Button>
          </div>
          <div className="mt-4 flex gap-2">
            <div className="flex-1">
              <Label>Code</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter or generate a code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="font-mono text-lg"
                />
                <Button variant="outline" size="icon" onClick={copyCode} disabled={!code}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promotion Type Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Promotional Code
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as PromotionType)}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="tool_calls" className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Tool Calls
              </TabsTrigger>
              <TabsTrigger value="chat_messages" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Chat Messages
              </TabsTrigger>
              <TabsTrigger value="tier_access" className="flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Tier Access
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tool_calls" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tool Calls Amount</Label>
                  <Input
                    type="number"
                    value={toolCallsAmount}
                    onChange={(e) => setToolCallsAmount(parseInt(e.target.value) || 0)}
                    min={1}
                    max={10000}
                  />
                </div>
                <div>
                  <Label>Expires After (months)</Label>
                  <Input
                    type="number"
                    value={expiresMonths}
                    onChange={(e) => setExpiresMonths(parseInt(e.target.value) || 12)}
                    min={1}
                    max={60}
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Users can stack multiple tool call promotions. Calls are consumed oldest-expiring
                first.
              </p>
            </TabsContent>

            <TabsContent value="chat_messages" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Chat Messages Amount</Label>
                  <Input
                    type="number"
                    value={chatMessagesAmount}
                    onChange={(e) => setChatMessagesAmount(parseInt(e.target.value) || 0)}
                    min={1}
                    max={10000}
                  />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <Switch checked={expiresWithCycle} onCheckedChange={setExpiresWithCycle} />
                  <Label>Expires with billing cycle</Label>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {expiresWithCycle
                  ? 'Bonus resets to 0 at end of billing cycle (use it or lose it)'
                  : 'Bonus persists across billing cycles until fully used'}
              </p>
            </TabsContent>

            <TabsContent value="tier_access" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Promoted Tier</Label>
                  <Select
                    value={String(promotedTierId)}
                    onValueChange={(v) => setPromotedTierId(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">Enthusiast</SelectItem>
                      <SelectItem value="3">Professional</SelectItem>
                      <SelectItem value="4">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Access Duration (days)</Label>
                  <Input
                    type="number"
                    value={promotionDays}
                    onChange={(e) => setPromotionDays(parseInt(e.target.value) || 14)}
                    min={1}
                    max={365}
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Only one tier promotion active at a time. New promotion replaces existing one.
              </p>
            </TabsContent>

            {/* Common Fields */}
            <div className="border-t pt-6 mt-6 space-y-4">
              <div>
                <Label>Description *</Label>
                <Textarea
                  placeholder="e.g., SETAC 2026 conference attendee bonus"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Valid for (days)</Label>
                  <Input
                    type="number"
                    value={validDays}
                    onChange={(e) => setValidDays(parseInt(e.target.value) || 30)}
                    min={1}
                    max={365}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How long code can be redeemed
                  </p>
                </div>
                <div>
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    placeholder="Unlimited"
                    value={maxUses || ''}
                    onChange={(e) => setMaxUses(e.target.value ? parseInt(e.target.value) : null)}
                    min={1}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty for unlimited</p>
                </div>
                <div>
                  <Label>Campaign Tag</Label>
                  <Input
                    placeholder="e.g., setac2026"
                    value={campaign}
                    onChange={(e) => setCampaign(e.target.value)}
                    maxLength={100}
                  />
                  <p className="text-xs text-muted-foreground mt-1">For tracking/filtering</p>
                </div>
              </div>
              <Button
                className="w-full mt-4"
                size="lg"
                onClick={handleCreate}
                disabled={isCreating || !code || !description}
              >
                {isCreating ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create {activeTab.replace('_', ' ')} Code
              </Button>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
